/*
 * URL contract for the client profile (`/clients/:clientId`):
 *
 *   ?tab=<ClientTab>                    active section (default: overview)
 *   ?dependentId=… (focus keys)         scroll to / open a record in a section
 *   ?open=kind:id  ?new=…               the Requests page that is open / a new-request action
 *
 * The Requests workspace owns `open` / `new` (see `features/requestsIde/routes.ts`);
 * the legacy `oi_*` keys the old Requests panel used are still recognised and
 * rewritten there so old deep links land. Pure helpers, no React: the hook
 * that binds them to the router lives in `hooks/useClientProfileUrlState.ts`.
 */

import {
  buildOpenSearch,
  LEGACY_INTAKE_QUERY_KEYS,
  NEW_SEARCH_PARAM,
  OPEN_SEARCH_PARAM,
  parseOpenParam,
  STAGE_SEARCH_PARAM,
  type OpenRequest
} from "../requestsIde/routes";

export const CLIENT_TABS = [
  "overview",
  "cases",
  "personal",
  "immigration_overview",
  "passports",
  "address_history",
  "employment",
  "education",
  "dependents",
  "documents",
  "outgoing_intakes",
  "reminders",
  "activity",
  "notes",
  "settings"
] as const;

export type ClientTab = (typeof CLIENT_TABS)[number];

export const DEFAULT_CLIENT_TAB: ClientTab = "overview";

export const FOCUS_QUERY_KEYS = [
  "dependentId",
  "reminderId",
  "addressHistoryId",
  "employmentHistoryId",
  "immigrationStatusHistoryId",
  "immigrationEntryExitHistoryId",
  "documentId",
  /** Personal: open the Autofill review. */
  "autofill",
  /** Settings: open the merge dialog on this other record. */
  "merge",
  /** Cases: open the create-case sheet. */
  "newCase",
  /** Notes: scroll to this entry. */
  "note",
  /** Overview: scroll to Portal Access. */
  "portal"
] as const;

export type FocusQueryKey = (typeof FOCUS_QUERY_KEYS)[number];

export type ClientFocusParams = Partial<Record<FocusQueryKey, string>>;

/** Query keys owned by the Requests IDE (current and legacy). */
export const INTAKE_QUERY_KEYS = [
  OPEN_SEARCH_PARAM,
  NEW_SEARCH_PARAM,
  STAGE_SEARCH_PARAM,
  ...LEGACY_INTAKE_QUERY_KEYS
] as const;

export type ClientProfileUrlState = {
  tab: ClientTab;
  /** Whether `?tab=` was present and valid (vs. defaulted). */
  explicitTab: boolean;
  focus: ClientFocusParams;
  /** The artifact the Requests IDE was asked to open, if any. */
  requestsOpen: OpenRequest | null;
};

export const isClientTab = (value: unknown): value is ClientTab =>
  typeof value === "string" &&
  (CLIENT_TABS as readonly string[]).includes(value.trim());

export const parseClientProfileSearch = (
  search: string
): ClientProfileUrlState => {
  const params = new URLSearchParams(search);
  const rawTab = (params.get("tab") ?? "").trim();
  const explicitTab = isClientTab(rawTab);

  const focus: ClientFocusParams = {};
  for (const key of FOCUS_QUERY_KEYS) {
    const raw = (params.get(key) ?? "").trim();
    if (raw) focus[key] = raw;
  }

  return {
    tab: explicitTab ? (rawTab as ClientTab) : DEFAULT_CLIENT_TAB,
    explicitTab,
    focus,
    requestsOpen: parseOpenParam(search)
  };
};

/**
 * Search string for switching to `tab`. Focus keys always reset; the Requests
 * keys survive only while staying on the Requests tab.
 */
export const buildClientProfileSearch = (
  currentSearch: string,
  tab: ClientTab,
  focus?: ClientFocusParams
): string => {
  const params = new URLSearchParams(currentSearch);
  params.set("tab", tab);
  for (const key of FOCUS_QUERY_KEYS) params.delete(key);
  if (tab !== "outgoing_intakes") {
    for (const key of INTAKE_QUERY_KEYS) params.delete(key);
  }
  if (focus) {
    for (const [key, value] of Object.entries(focus)) {
      const v = (value ?? "").trim();
      if (v) params.set(key, v);
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

export type CreateIntakeOptions = {
  /** A catalog template to start the draft from. */
  templateHint?: string;
  /** @deprecated The template library filter of the old panel; ignored. */
  workflowFilter?: string;
};

/**
 * Search string that opens the Requests IDE ready to start a request: with a
 * template hint, a draft from that template; otherwise the Client Manager's
 * composer, seeded to build one.
 */
export const buildCreateIntakeSearch = (
  currentSearch: string,
  opts?: CreateIntakeOptions
): string => {
  const hint = (opts?.templateHint ?? "").trim();
  return buildOpenSearch(
    currentSearch,
    null,
    hint ? { kind: "templates", templateIds: [hint] } : { kind: "ai" }
  );
};

/**
 * Search string for a documents-only request scoped to a visa workflow.
 */
export const buildDocsOnlyIntakeSearch = (
  currentSearch: string,
  visaWorkflow: string
): string | null => {
  const visa = (visaWorkflow ?? "").trim();
  if (!visa) return null;
  return buildOpenSearch(currentSearch, null, { kind: "docs", visa });
};

/** Search string for the Requests tab with nothing asked of it. */
export const buildIntakesBaseSearch = (currentSearch: string): string => {
  const params = new URLSearchParams(currentSearch);
  params.set("tab", "outgoing_intakes");
  for (const key of INTAKE_QUERY_KEYS) params.delete(key);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

/** Search string that opens one intake in the Requests IDE. */
export const buildOpenIntakeSearch = (
  currentSearch: string,
  intakeId: string
): string => buildOpenSearch(currentSearch, { kind: "intake", id: intakeId });
